import { Button, Theme } from '@mui/material';
import styled from 'styled-components';

import { getThemeValue } from '@/shared/components/ui/button/common/button-common.utils';

import {
  TSelectButtonVariant,
  TStyledSelectButton,
} from './select-button.types';

const VARIANT_STYLES = {
  primary: {
    textColor: 'text.primary',
    textActiveColor: 'primary.main',
    iconColor: 'primary.main',
    arrowColor: 'selectButton.arrowColor.primary',
    typography: 'p8',
    activeTypography: 'p9',
  },
  secondary: {
    textColor: 'text.secondary',
    textActiveColor: 'text.primary',
    iconColor: 'text.secondary',
    arrowColor: 'selectButton.arrowColor.secondary',
    typography: 'button2',
    activeTypography: 'button1',
  },
} as const;

// TSelectButtonVariant derives from the theme type, so it can widen past
// VARIANT_STYLES. Resolving through the default keeps render safe either way.
const variantStyles = (variant: TSelectButtonVariant = 'secondary') =>
  VARIANT_STYLES[variant] ?? VARIANT_STYLES.secondary;

export const StyledSelectedButton = styled(Button)<TStyledSelectButton>`
  && {
    display: flex;
    gap: 0.375rem;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.5rem;
    height: ${({ theme }) => theme.shape.buttonHeight.extraSmall};
    border-radius: ${({ theme }) => theme.shape.buttonRadius.extraSmall};
    text-wrap: nowrap;
    min-width: fit-content;

    color: ${({ theme, $variant, $active }) =>
      getThemeValue(
        theme as Theme,
        variantStyles($variant)[$active ? 'textActiveColor' : 'textColor'],
      )};

    ${({ theme, $variant, $active }) =>
      theme.typography[
        variantStyles($variant)[$active ? 'activeTypography' : 'typography']
      ]};

    background-color: ${({ theme, $clicked }) =>
      $clicked ? theme.palette.background.clickableHover : 'transparent'};

    &:hover {
      background-color: ${({ theme }) =>
        theme.palette.background.clickableHover};
    }

    svg.select-button-icon,
    svg.select-button-arrow {
      width: 1rem;
      height: 1rem;
    }

    svg.select-button-icon {
      color: ${({ theme, $variant }) =>
        getThemeValue(theme as Theme, variantStyles($variant).iconColor)};
    }

    svg.select-button-arrow {
      color: ${({ theme, $variant }) =>
        getThemeValue(theme as Theme, variantStyles($variant).arrowColor)};
    }

    &:disabled {
      color: ${({ theme }) => theme.palette.text.disabled};
      svg {
        color: ${({ theme }) => theme.palette.text.disabled};
      }
    }
  }
`;
